/**
 * Voice Input utilities for AI Prescription
 * These utilities handle WebSocket connections, audio processing, and payload creation
 * for the VoiceSearchInput component used in AI Prescription
 */

import { buildOrchestrationProcessPayload } from "../orchestrationPayload";
import {
  getJwtTokenCookie,
  getWsAuthToken,
  shouldUseServerSessionAuth,
} from "../authCookie";
import { getReactEnvValue, isReactStrictEnv } from "../envConfig";
import { getPatientId } from "../patientSession";

// Constants
export const MAX_UPDATE_RATE = 16; // Max 60 updates per second (16ms between updates) - prevents overload
export const MAX_TEXT_LENGTH = 10000; // Maximum text length to prevent memory issues

// WebSocket URL configuration.
// No hardcoded staging fallback: an unset REACT_APP_API_URL must never silently point
// production traffic at a real staging environment. In a strict (dev/stage/prod) build
// this throws immediately so the misconfiguration is caught at load time instead of
// quietly leaking a hub-stg URL; in local/test envs it falls back to an obviously-invalid
// placeholder so voice dictation fails fast and visibly rather than "working" against a
// real remote hub.
function resolveVoiceApiUrl(): string {
  const explicit = getReactEnvValue("REACT_APP_API_URL");
  if (explicit) {
    return explicit;
  }
  if (isReactStrictEnv()) {
    throw new Error(
      "REACT_APP_API_URL is not set. Refusing to fall back to a hardcoded staging URL " +
        "outside local/dev."
    );
  }
  return "ws://invalid.local.not-configured";
}

export const VOICE_API_URL = resolveVoiceApiUrl();
export const VOICE_WS_URL = VOICE_API_URL.replace(/^https?/, (match) =>
  match === "https" ? "wss" : "ws"
);

// Construct WebSocket URL - handle both cases where base URL includes /ws or not
export const getWebSocketUrl = async (): Promise<string> => {
  // Remove trailing slashes
  const baseUrl = VOICE_WS_URL.replace(/\/+$/, '');
  // If base URL already ends with /ws, just append /transcribe
  const url = baseUrl.endsWith('/ws') ? `${baseUrl}/transcribe` : `${baseUrl}/ws/transcribe`;
  const authToken = shouldUseServerSessionAuth()
    ? await getWsAuthToken()
    : getJwtTokenCookie();
  const authParam = shouldUseServerSessionAuth() ? "wsToken" : "jwtToken";
  const urlWithAuth = authToken
    ? `${url}${url.includes("?") ? "&" : "?"}${authParam}=${encodeURIComponent(authToken)}`
    : url;
  if (isReactStrictEnv() && url.startsWith("ws://")) {
    throw new Error("Voice WebSocket URL must use wss:// outside local/dev");
  }
  return urlWithAuth;
};

export const getWebSocketProtocols = (): string[] | undefined => {
  // Transcription supports jwtToken query auth for compatibility with currently
  // deployed hubs that do not echo Sec-WebSocket-Protocol.
  return undefined;
};

/** Static worklet served from public/ — must not use blob: URLs (blocked by CSP). */
export const getAudioWorkletModuleUrl = (): string => {
  const publicUrl = process.env.PUBLIC_URL || "";
  const path =
    !publicUrl || publicUrl === "."
      ? "/audio-processor.js"
      : `${publicUrl.replace(/\/$/, "")}/audio-processor.js`;
  if (typeof window !== "undefined") {
    return new URL(path, window.location.origin).href;
  }
  return path;
};

// Helper function to create processing payload (DRY principle)
export const createProcessingPayload = (text: string, metadata?: any) => {
  const loginUserIdRaw =
    (typeof metadata?.loginUserId === "string" && metadata.loginUserId.trim()) ||
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("LoginUserID")?.trim()) ||
    "";
  return buildOrchestrationProcessPayload({
    type: "process",
    text,
    patientUniqueIdentifier:
      metadata?.patientUniqueIdentifier || getPatientId(),
    patientName: metadata?.patientName,
    jwtToken: metadata?.jwtToken,
    doctorId: metadata?.doctorId || "",
    providerLocationId: metadata?.providerLocationId || "",
    sessionId: metadata?.sessionId,
    vttContent: metadata?.vttContent,
    loginUserId: loginUserIdRaw.trim(),
  });
};

